import React, {useContext, useEffect, useState} from "react";
import {
    Box, Chip,
    Fade,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {NodeType} from "../../../../model/ModelData";
import {editMenuItems, menuItemsInfo} from "./editMenuItems";
import {IconMenu, IconPlus, IconTrash, IconCopy} from "@tabler/icons-react";
import useConfirm from "../../../../hooks/useConfirm";
import {GreetMessageTab} from "./greet-messages/GreetMessageTab";
import {SettingsTab} from "./settings/SettingsTab";
import {StaticRoutesTab} from "./routes/StaticRoutesTab";
import {DynamicRoutesTab} from "./routes/DynamicRoutesTab";
import {KnowledgeBaseTab} from "./knowledge-base/KnowledgeBaseTab";
import {VarsTab} from "./vars/VarsTab";
import {PleaseWaitMessagesTab} from "./please-wait-messages/PleaseWaitMessagesTab";
import {SlotsTab} from "./slots/SlotsTab";
import {OutputParserTab} from "./output-parser/OutputParserTab";
import {LLMBoostTab} from "./llm-booster/LLMBoostTab";
import {getRoutes} from "../../../../utils/nodeMapper";
import {FilterTab} from "./filter/FilterTab";
import {SettingsBoosterTab} from "./settings-booster/SettingsBoosterTab";
import {InputParamsTab} from "./input_params/InputParamsTab";
import {OutputParamsTab} from "./output_params/OutputParamsTab";
import {SettingsToolsTab} from "./settings-tools/SettingsToolsTab";
import {SupervisionLearningTab} from "./supervision_learning/SupervisionLearningTab"
import {TagSelectionModal} from "./components/tag-selection-modal";
import {Context as AppContext} from "../../../../context/AppContext";
import {InputArgsTab} from "./input-args/InputArgsTab";
import {SettingsRefactoredTab} from "./settings/SettingsRefactoredTab"
import {LLMConfigurationTab} from "./llm-configuration/LllmConfigurationTab"
import {VoiceParametersTab} from "./voice-parameters/VoiceParametersTab"

export const EditNodeMenu = ({
                                 open,
                                 selectedNode,
                                 selectedRoute,
                                 nodes,
                                 onDuplicate,
                                 onClose,
                                 onUpdate,
                                 onRemove,
                                 project,
                                 flow,
                                 showHideMenuButton = true
                             }) => {
    const [node, setNode] = useState(selectedNode);
    const [items, setItems] = useState([]);
    const [value, setValue] = useState("");

    useEffect(() => {
        if (selectedNode) {
            let newItems = editMenuItems[selectedNode.type] || [];
            setItems(newItems);
            if (selectedNode?.id !== node?.id) {
                setValue("");
            }
        }
        setNode(selectedNode || null)
    }, [selectedNode])

    useEffect(() => {
        if (selectedRoute) {
            const routes = getRoutes(node?.routes);
            const route = routes?.find(r => r?.id === selectedRoute);
            if (route) {
                setValue(route?.variant)
            }
        }
    }, [selectedRoute, node])

    const onChangeData = (key, value) => {
        setNode(prev => {
            const state = {...prev};
            state.data[key] = value;

            return state;
        })
    }

    return (
        open && node &&
        <>
            {/* Left menu wrapper — this is the source of the fixed height.
          width stays locked at 310px so it never stretches full-page,
          and maxHeight is an absolute value (not a %) so it doesn't
          depend on any ancestor having an explicit height. */}
            <Stack
                direction={"row"}
                sx={{width: "310px", height: "100%", maxHeight: "calc(100dvh - 48px)"}}
            >
                <MenuItems node={node} setNode={setNode} onUpdate={onUpdate} value={value} setValue={setValue}
                           items={items}
                           showHideMenuButton={showHideMenuButton}
                           project={project}
                           flow={flow}
                           onDuplicate={onDuplicate}
                           onRemove={onRemove} onClose={onClose}/>

            </Stack>
            {
                value &&
                <Fade
                    in={!!value}
                    timeout={500}
                >
                    {/* Right tab-content panel — independent of the left menu,
              carries its own absolute maxHeight + overflowY so its
              contents scroll internally instead of the whole page. */}
                    <Stack direction={"column"} gap={2} sx={{
                        p: 1, pb: 10,
                        minWidth: "280px",
                        maxWidth: "320px",
                        width: "100%",
                        maxHeight: "calc(100dvh - 48px)",
                        borderRight: 1,
                        overflowY: "auto",
                        borderColor: value ? "divider" : "transparent"
                    }}>
                        {
                            {
                                "vars": <VarsTab node={node} setNode={setNode} project={project} onUpdate={onUpdate}/>,
                                "greet": <GreetMessageTab node={node} setNode={setNode} onUpdate={onUpdate}/>,
                                "please_wait_messages": <PleaseWaitMessagesTab node={node} setNode={setNode}
                                                                               onUpdate={onUpdate}/>,
                                "slots": <SlotsTab node={node} setNode={setNode} onUpdate={onUpdate} project={project}
                                                   onChangeData={onChangeData}/>,
                                "settings": <SettingsTab node={node} setNode={setNode} onUpdate={onUpdate} nodes={nodes}
                                                         project={project}/>,
                                "supervision_learning": <SupervisionLearningTab node={node} setNode={setNode}
                                                                                nodes={nodes} onUpdate={onUpdate}
                                                                                onChangeData={onChangeData}
                                                                                project={project}/>,
                                "settings_booster": <SettingsBoosterTab node={node} setNode={setNode} nodes={nodes}
                                                                        onUpdate={onUpdate}
                                                                        onChangeData={onChangeData} project={project}/>,
                                "settings_tools": <SettingsToolsTab node={node} setNode={setNode} nodes={nodes}
                                                                    onUpdate={onUpdate}
                                                                    onChangeData={onChangeData} project={project}/>,
                                "knowledge_base": <KnowledgeBaseTab node={node} setNode={setNode} onUpdate={onUpdate}
                                                                    project={project}/>,
                                "output_parser": <OutputParserTab node={node} setNode={setNode} onUpdate={onUpdate}
                                                                  onChangeData={onChangeData} project={project}/>,
                                "input_params": <InputParamsTab node={node} setNode={setNode} onUpdate={onUpdate}
                                                                onChangeData={onChangeData} project={project}/>,
                                "output_params": <OutputParamsTab node={node} setNode={setNode} onUpdate={onUpdate}
                                                                  onChangeData={onChangeData} project={project}/>,
                                "llm_boost": <LLMBoostTab node={node} setNode={setNode} onUpdate={onUpdate}
                                                          onChangeData={onChangeData}
                                                          project={project}/>,
                                "static": <StaticRoutesTab node={node} setNode={setNode} nodes={nodes}
                                                           onUpdate={onUpdate}
                                                           flow={flow} project={project}
                                                           selectedRoute={selectedRoute}/>,
                                "dynamic": <DynamicRoutesTab node={node} setNode={setNode} nodes={nodes}
                                                             onUpdate={onUpdate}
                                                             project={project} flow={flow}
                                                             selectedRoute={selectedRoute}/>,
                                "filter": <FilterTab node={node} setNode={setNode} nodes={nodes} onUpdate={onUpdate}
                                                     project={project} flow={flow} selectedRoute={selectedRoute}/>,
                                "input_args": <InputArgsTab node={node} setNode={setNode} nodes={nodes}
                                                            onUpdate={onUpdate}
                                                            project={project} flow={flow}
                                                            selectedRoute={selectedRoute}/>,
                                "settings_refactored": <SettingsRefactoredTab node={node} setNode={setNode}
                                                                              onUpdate={onUpdate}></SettingsRefactoredTab>,
                                "llm_configuration": <LLMConfigurationTab node={node} setNode={setNode}
                                                                          onUpdate={onUpdate} project={project}
                                                                          nodes={nodes}></LLMConfigurationTab>,
                                "voice_parameters": <VoiceParametersTab node={node} setNode={setNode}
                                                                        onUpdate={onUpdate} project={project}
                                                                        nodes={nodes}></VoiceParametersTab>
                            }[value]
                        }
                    </Stack>
                </Fade>
            }
        </>
    )
}

const MenuItems = ({
                       node,
                       setNode,
                       onUpdate,
                       onDuplicate,
                       value,
                       setValue,
                       items,
                       onRemove,
                       onClose,
                       showHideMenuButton,
                       project,
                       flow
                   }) => {
    const [openTags, setOpenTags] = useState(false);
    const [copyAnchor, setCopyAnchor] = useState(null);
    const {updateProject} = useContext(AppContext);

    const handleCopyTo = (targetFlowId) => {
        setCopyAnchor(null);
        onDuplicate(node?.id, targetFlowId);
        onClose();
    };
    const confirmDelete = useConfirm(
        'Are you sure you want to delete this node?',
        node?.name || ""
    );

    const onDelete = async () => {
        const response = await confirmDelete();
        if (response) {
            onRemove(node?.id);
            onClose();
        }
    }

    const handleDeleteTag = async (uid) => {

        const newProject = {
            ...project,
            settings: {
                ...project.settings,
                tags: project?.settings?.tags?.map(tag => {
                    if (tag?.id === uid && tag?.nodes?.includes(node?.id)) {
                        return {...tag, nodes: tag?.nodes?.filter(i => i !== node?.id)}
                    }
                    return {...tag}
                })
            }
        };

        await updateProject(newProject);
    }


    const nodeTags = project?.settings?.tags?.filter(i => i?.nodes?.includes(node?.id));

    return (
        // This Stack sits directly under the fixed-height wrapper in
        // EditNodeMenu, so height: "100%" here resolves against that
        // absolute maxHeight instead of bubbling up to the viewport.
        // overflow: "hidden" keeps this container itself from scrolling —
        // only the List below (flex: 1 + overflowY: auto) should scroll.
        <Stack direction={"column"} gap={2}
               sx={{
                   p: 1, width: "100%", height: "100%",
                   overflow: "hidden",
                   borderRight: 1, borderColor: value ? "divider" : "transparent"
               }}>
            <TagSelectionModal open={openTags} handleClose={() => setOpenTags(false)} node={node} onUpdate={onUpdate}
                               project={project}/>
            <Stack direction={"row"} gap={1} alignItems={"center"} sx={{width: "100%"}}>
                {
                    showHideMenuButton &&
                    <IconButton size={"small"} onClick={onClose}>
                        <IconMenu size={17}/>
                    </IconButton>
                }
                <Typography textTransform={"capitalize"} fontWeight={700} sx={{flex: 1}}>
                    {node?.type?.replaceAll("_", " ")}
                </Typography>
                <IconButton
                    size={"small"}
                    onClick={(e) => setCopyAnchor(e.currentTarget)}
                    disabled={node?.type === NodeType.START || node?.type === NodeType.START_FORM || node?.type === NodeType.END_FORM}
                >
                    <IconCopy size={17}/>
                </IconButton>
                <Menu
                    anchorEl={copyAnchor}
                    open={!!copyAnchor}
                    onClose={() => setCopyAnchor(null)}
                    slotProps={{paper: {sx: {minWidth: 150, maxHeight: 320}}}}
                    MenuListProps={{dense: true, sx: {py: 0.5}}}
                >
                    <Typography sx={{px: 2, py: 0.5, fontSize: "0.7rem", color: "text.secondary"}}>Copy to</Typography>

                    <MenuItem onClick={() => handleCopyTo(flow?.id)}
                              sx={{minHeight: 0, py: 0.5, fontSize: "0.78rem"}}>
                        <ListItemIcon sx={{minWidth: "26px !important"}}><IconCopy size={15}/></ListItemIcon>
                        <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>This flow</ListItemText>
                    </MenuItem>

                    {(project?.flows || [])
                        .filter(f => f.id !== flow?.id)
                        .map(f => (
                            <MenuItem key={f.id} onClick={() => handleCopyTo(f.id)}
                                      sx={{minHeight: 0, py: 0.5, fontSize: "0.78rem"}}>
                                <ListItemIcon sx={{minWidth: "26px !important"}}><IconCopy size={15}/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: "0.78rem"}}>{f.name}</ListItemText>
                            </MenuItem>
                        ))}
                </Menu>
                <IconButton
                    size={"small"} color={"error"}
                    onClick={onDelete}
                    disabled={node?.type === NodeType.START || node?.type === NodeType.START_FORM || node?.type === NodeType.END_FORM}
                >
                    <IconTrash size={17}/>
                </IconButton>
            </Stack>
            <TextField
                size={"small"}
                value={node?.name}
                label={"Name"}
                onChange={(e) => setNode(prev => {
                    const state = {...prev};
                    state.name = e.target.value;
                    return state;
                })}
                onBlur={() => onUpdate(node)}
            />
            <Stack direction={"row"} gap={1} alignItems={"center"} useFlexGap flexWrap={"wrap"}>
                {nodeTags?.map(tag => {

                    return <Chip
                        key={tag?.id} label={tag?.name || tag?.id}
                        sx={{p: 0.5, fontSize: 12}} size={"small"}
                        onDelete={() => handleDeleteTag(tag?.id)}
                    />
                })}
                <Chip
                    label={"Add Tag"} size={"small"}
                    icon={<IconPlus size={18}/>}
                    sx={{p: 0.5, fontSize: 12}}
                    onClick={() => setOpenTags(true)}
                />
            </Stack>
            {/* flex: 1 lets this take up whatever height is left after the
          header/name/tags above. minHeight: 0 is required — without it
          a flex item won't shrink below its content size, and
          overflowY: auto would silently do nothing. */}
            <List sx={{my: 0, py: 0, flex: 1, overflowY: "auto", minHeight: 0}}>
                {
                    items?.map(item => {
                        const {primary, secondary} = menuItemsInfo[item];
                        const isSelected = value === item;
                        if (!node?.data?.use_llm && item === "llm_boost") {
                            return null;
                        }
                        return (
                            <ListItemButton
                                key={item}
                                onClick={() => setValue(isSelected ? "" : item)}
                                sx={{
                                    p: 0.5,
                                    bgcolor: isSelected ? "rgba(220,228,234, 0.5)" : "transparent",
                                    "&:hover": {bgcolor: "rgba(220,228,234, 0.5)"}
                                }}
                            >
                                <ListItemText primary={primary} secondary={secondary}
                                              primaryTypographyProps={{
                                                  fontWeight: 700,
                                                  fontSize: 13,
                                                  textTransform: "capitalize"
                                              }}
                                              secondaryTypographyProps={{fontSize: 11}}/>
                            </ListItemButton>
                        )
                    })
                }
            </List>
        </Stack>
    )
}